import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { navigate } from "@reach/router";
import GoogleLogin from "react-google-login";
import { Formik, Form } from "formik";
import { CircularProgress } from "@material-ui/core";
import { useSpring, animated } from "react-spring";
// import { Keyframes } from "react-spring/renderprops";
// import useInterval from "react-useinterval";

import { Common, User } from "../../store";
import FormTextField from "../Common/FormElements/FormTextField";
import {
  magicCodeAuthValidationSchema,
  magicCodeValidationSchema,
} from "../../validations/authValidation";
import useAlert from "../../Hooks/useAlert";
import useAuth from "../../Hooks/useAuth";

import COLORS from "../../assets/colors";
import { H1, Paragraph } from "../Common/Typography";
import { Container } from "../Common/GridSystem";
import { PrimaryButton } from "../Common/Buttons/PrimaryButton";
import { SecondaryButton } from "../Common/Buttons/SecondaryButton";
import { Images } from "./../../assets/images";
import { AuthService } from "../../services/AuthService";
import { SkillStatusMenu, SkillStatusMenuSME } from "../utils";
import useFullLoader from "../../Hooks/useFullLoader";

export default function Login(props) {
  const {
    setIsUserLoggedIn,
    setUserDetails,
    setSkillStatusMenuList,
  } = useAuth();
  const { showAlert } = useAlert();
  const { setFullLoader } = useFullLoader();
  const [showMagicCodeForm, setShowMagicCodeForm] = useState(false);
  const [isMagicCodeEntry, setIsMagicCodeEntry] = useState(false);
  const intermediateToken = useRef(null);
  const validatedToken = useRef(null);
  const loginActive = useRef(true);
  const googleLoginCompleted = useRef(false);
  const isMagicCodeActive = () => loginActive.current && !googleLoginCompleted.current;

  console.log("props", props);

  const loginBoxAnimate = useSpring({
    marginLeft: 30,
    opacity: 0.95,
    from: { marginLeft: -100, opacity: 0.8 },
    config: { duration: 500 },
  });

  const logoAnimate = useSpring({
    transform: `scale(1)`,
    opacity: 1,
    from: { transform: `scale(0.5)`, opacity: 0 },
    config: { duration: 500 },
    reset: true,
  });

  useEffect(() => {
    User.clearAll();
    Common.clearAll();
    return () => {
      loginActive.current = false;
    };
  }, []);

  // useInterval(increaseCount, 1000, 5);

  const completeLogin = (res) => {
    const resModify = {
      ...res,
      User_Type: res.userType.userTypeId,
      userTypeName: res.userType.userType,
    };
    User.setUserDetails(resModify);
    setUserDetails(resModify);
    setIsUserLoggedIn(true);
    showAlert(`Welcome ${resModify.name}`, "success", 1500);
    if (!!props.location?.state?.redirectPath) {
      navigate(props.location?.state?.redirectPath);
    } else {
      navigate("/");
    }

    if (resModify.User_Type === 3) {
      setSkillStatusMenuList(SkillStatusMenuSME);
    } else {
      setSkillStatusMenuList(SkillStatusMenu);
    }
  };

  const handleGoogleSignIn = async (payload) => {
    setFullLoader(true);
    try {
      const { tokenId } = payload;
      const res = await AuthService.accountLoginV3({ tokenId });
      if (!loginActive.current) return;
      googleLoginCompleted.current = true;
      completeLogin(res);
    } catch (error) {
      showAlert(error.error, "error", 1500);
    } finally {
      setFullLoader(false);
    }
  };

  const handleGoogleFailSignIn = (msg) => console.log(msg);

  const handleGetMagicCode = async ({ businessEmail }, { setStatus, setSubmitting }) => {
    intermediateToken.current = null;
    setStatus(undefined);
    try {
      const response = await AuthService.requestSkillsArchitectMagicCode(businessEmail);
      if (!isMagicCodeActive()) return;
      if (response?.success === true) {
        intermediateToken.current = typeof response.intermediateToken === "string"
          ? response.intermediateToken || null
          : null;
        setIsMagicCodeEntry(Boolean(intermediateToken.current));
      }
      setStatus(response?.message || "Unable to request a Magic Code. Please try again.");
    } catch (error) {
      if (isMagicCodeActive()) {
        setStatus(error?.response?.data?.message || "Unable to request a Magic Code. Please try again.");
      }
    } finally {
      if (isMagicCodeActive()) setSubmitting(false);
    }
  };

  const exchangeValidatedMagicCode = async () => {
    if (!isMagicCodeActive()) return;
    setFullLoader(true);
    try {
      const res = await AuthService.magiccodeLoginv3({ code: validatedToken.current });
      if (isMagicCodeActive()) completeLogin(res);
    } catch (error) {
      if (isMagicCodeActive()) {
        showAlert(error?.error || error?.message || "Unable to log in with Magic Code.", "error", 1500);
      }
    } finally {
      setFullLoader(false);
    }
  };

  const handleValidateMagicCode = async ({ magicCode }, { setStatus, setSubmitting }) => {
    if (!intermediateToken.current) {
      setStatus("Request a new Magic Code before validating it.");
      setSubmitting(false);
      return;
    }

    setStatus(undefined);
    try {
      if (!validatedToken.current) {
        const response = await AuthService.validateSkillsArchitectMagicCode(
          magicCode,
          intermediateToken.current
        );
        if (!isMagicCodeActive()) return;
        if (response?.success !== true || typeof response.token !== "string" || !response.token) {
          setStatus(response?.message || "The Magic Code could not be validated. Please try again.");
          return;
        }
        validatedToken.current = response.token;
      }

      await exchangeValidatedMagicCode();
    } catch (error) {
      if (isMagicCodeActive()) {
        setStatus(
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "The Magic Code could not be validated. Please check it and try again."
        );
      }
    } finally {
      if (isMagicCodeActive()) setSubmitting(false);
    }
  };

  return (
    <LoginWrapper>
      <Container>
        <img className="dotsIcon" src={Images.icons.dotsIcon} alt="dots" />
        <animated.div style={logoAnimate} className="logLogoIcon">
          <Paragraph>Powered by</Paragraph>
          <img src={Images.logo.logoSVG} alt="logo" />
        </animated.div>
        <animated.div style={loginBoxAnimate} className="login-boxing">
          <H1>Welcome to Skills Architect</H1>
          <img
            className="cubesIcon"
            src={Images.icons.cubesIcon}
            alt="cubesIcon"
          />
          <ButtonBlock>
            <div className="social-button">
              <SecondaryButton
                className="google-btn-overlap"
                id="googleButton"
                style={{
                  borderColor: COLORS.BLUE,
                  backgroundColor: COLORS.BLUE,
                  color: "white",
                  width: "100%",
                }}
              >
                <img
                  src={Images.icons.googleIcon}
                  alt="google"
                  style={{ marginRight: 10 }}
                />
                Login with Disprz
              </SecondaryButton>
              <GoogleLogin
                clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
                buttonText="Login with Disprz"
                onSuccess={(res) => handleGoogleSignIn(res)}
                onFailure={(msg) => handleGoogleFailSignIn(msg)}
                cookiePolicy={"single_host_origin"}
                className="google-btn"
              />
            </div>
            <Paragraph>Or</Paragraph>
            <PrimaryButton
              type="button"
              onClick={() => setShowMagicCodeForm(true)}
              aria-expanded={showMagicCodeForm}
              aria-controls="magic-code-form"
              style={{ width: "100%" }}
            >
              Login with Magic Code
            </PrimaryButton>
            {showMagicCodeForm && (
              <Formik
                initialValues={{ businessEmail: "", magicCode: "" }}
                validationSchema={isMagicCodeEntry
                  ? magicCodeAuthValidationSchema
                  : magicCodeValidationSchema}
                onSubmit={isMagicCodeEntry ? handleValidateMagicCode : handleGetMagicCode}
              >
                {({ values, status, isSubmitting, handleChange, setFieldValue, setStatus, resetForm }) => (
                  <Form id="magic-code-form" noValidate>
                    {!isMagicCodeEntry && (
                      <FormTextField
                        id="businessEmail"
                        name="businessEmail"
                        type="email"
                        label="Business Email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        autoFocus
                        disabled={isSubmitting}
                        onChange={(event) => {
                          intermediateToken.current = null;
                          setStatus(undefined);
                          handleChange(event);
                        }}
                      />
                    )}
                    {isMagicCodeEntry && (
                      <FormTextField
                        id="magicCode"
                        name="magicCode"
                        type="text"
                        label="6-digit Magic Code"
                        placeholder="Enter your code"
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        inputProps={{ maxLength: 6, pattern: "[0-9]{6}" }}
                        autoFocus
                        disabled={isSubmitting}
                        onChange={(event) => {
                          validatedToken.current = null;
                          setStatus(undefined);
                          setFieldValue("magicCode", event.target.value.replace(/\D/g, "").slice(0, 6));
                        }}
                      />
                    )}
                    <PrimaryButton
                      type="submit"
                      disabled={isSubmitting}
                      aria-busy={isSubmitting}
                      style={{ width: "100%", position: "relative" }}
                    >
                      {isMagicCodeEntry ? "Validate Magic Code" : "Get Magic Code"}
                      {isSubmitting && isMagicCodeEntry && (
                        <ButtonLoadingMask aria-hidden="true">
                          <CircularProgress size={20} color="inherit" />
                        </ButtonLoadingMask>
                      )}
                    </PrimaryButton>
                    {status && <Paragraph role="status">{status}</Paragraph>}
                    {isMagicCodeEntry && (
                      <SecondaryButton
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => {
                          intermediateToken.current = null;
                          validatedToken.current = null;
                          resetForm({ values: { ...values, magicCode: "" }, status: undefined });
                          setIsMagicCodeEntry(false);
                        }}
                        style={{ width: "100%", color: "white", borderColor: "white" }}
                      >
                        Request a new code / change email
                      </SecondaryButton>
                    )}
                  </Form>
                )}
              </Formik>
            )}
          </ButtonBlock>
        </animated.div>
      </Container>
    </LoginWrapper>
  );
}

const LoginWrapper = styled.div`
  background: url(${Images.bgImgs.meetingImg}) center no-repeat;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  .dotsIcon {
    top: 60px;
    position: absolute;
    right: 180px;
  }
  .logLogoIcon {
    bottom: 45px;
    position: absolute;
    right: 45px;
    text-align: center;
    img {
      width: 100px;
    }
    p {
      color: ${COLORS.PRIMARY_WHITE};
      font-size: 12px;

      padding-bottom: 0px;
    }
  }
  .login-boxing {
    background: linear-gradient(167deg, #11213b 2%, #5f099f 94%);
    border-radius: 50px;
    border-bottom-right-radius: 0px;
    margin-left: 30px;
    opacity: 0.95;
    padding: 90px 54px;
    position: relative;
    width: 420px;
    .cubesIcon {
      bottom: 10px;
      position: absolute;
      right: -100px;
    }
    form > div {
      padding-bottom: 35px;
      &:last-child {
        padding-bottom: 0px;
      }
    }
    label {
      color: ${COLORS.PRIMARY_WHITE};
      font-size: 16px;
      font-weight: 600px;
    }
    .MuiInputBase-root * {
      /* background: red !important; */
    }
    fieldset {
      border: none;
      border-bottom: solid 1px #7576a8;
      border-radius: 0px;
      top: -6px;
    }
    .MuiOutlinedInput-input {
      color: #e2e3fd;
      -webkit-appearance: none;
    }
    h1 {
      color: white;
      padding-bottom: 30px;
      font-size: 24px;
      line-height: 30px;
      small {
        color: inherit;
        display: block;
        font-size: 20px;
        font-weight: normal;
      }
    }
  }
`;

const ButtonBlock = styled.div`
  padding: 20px 0px;
  text-align: center;
  flex-direction: column;
  button {
    border-radius: 22px;
    height: 44px;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 12px;
  }
  .social-button {
    position: relative;
    .google-btn {
      border-radius: 22px !important;
      position: absolute;
      left: 0;
      opacity: 0 !important;
      width: 100%;
    }
  }
  a {
    pointer-events: none;
  }
  p {
    font-size: 12px;
    padding-bottom: 10px;
  }
  a,
  p {
    color: #e2e3fd;
  }
`;

const ButtonLoadingMask = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  background: rgba(25, 25, 45, 0.72);
  color: white;
`;
